import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import { checkIcon, copyIcon, editIcon, eyeIcon } from '../../../config/variables';
import { navigateRoute } from '../../../utils/HelperFunctions';
import { ITableContainerProps } from '../../../types/Interfaces';
import { IVehicle } from '../types/interfaces';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import { generalCreate } from '../../../API/api';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useState } from 'react';
import { authContext } from '../../../store/context/authContext';
import TableSkeleton from '../../../components/loaders/TableSkeleton';

const VehiclesTableContainer = ({ tableHeaders, data, apiDataLoading, noDataMessage, lessColumns, used }: ITableContainerProps<IVehicle[]> & { used?: boolean }) => {
  const navigate = useNavigate();
  const {i18n} = useTranslation()
  const isDesktop = useMediaQuery({ query: '(min-width: 1024px)' });
  const queryClient = useQueryClient()
  const [loadingItems, setLoadingItems] = useState<{ [key: string]: boolean }>({});
  const [statuses, setStatuses] = useState<{ [key: string]: boolean }>({});
  const [copied, setCopied] = useState<Record<number, boolean>>({});
  const {t} = useTranslation()
  const { catchError } = useContext(authContext);

  useEffect(() => {
    const statusesData: any = {}
    data?.forEach((item: any) => {
      statusesData[item?.id] = item?.is_active
    })

    setStatuses(statusesData)
  }, [data])

  const handleChangeStatus = (item: any) => {
    setLoadingItems((prev) => ({ ...prev, [item.id]: true }));
    let value = { vehicles_ids: [item?.id], _method: 'POST' };

    let route = `/admin/vehicles/toggle-activation`;
    generalCreate({
      route: route,
      values: { ...value },
      method: "patch"
    })
      .then((res) => {
        setLoadingItems((prev) => ({ ...prev, [item.id]: false }));
        toast.success(`${t('status_changed_successfully')}`);
        setStatuses(prev => ({...prev, [item.id]: !item?.is_active}))
        queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      })
      .catch((error) => {
        setLoadingItems((prev) => ({ ...prev, [item.id]: false }));
        catchError(error);
      });
  };

  if (apiDataLoading) return <TableSkeleton columns={6} withoutButton withoutHeader />;

  return (
    <div className={`table_container ${lessColumns && isDesktop && 'lessColumns'} hasPagination`}>
      <div className="table_header">
        {tableHeaders?.map((header: any, index: number) => (
          <span className={`head ${header.customClass}`} key={index}>
            {header.label}
          </span>
        ))}
      </div>
      <div className={`table_data ${(!data || data?.length === 0) && 'no_data'}`}>
        {data?.length > 0 ? (
          <>
            {data?.map((item: any, index: number) => {
              let isCopied = copied[item.id] || false;
              return (
              <div
                className={`item ${used ? '' : 'clickable'} has_logo vehicles`}
                key={item?.id}
                onClick={(e) => {
                  if (used) return;
                  navigateRoute(e, `/vehicles/${item.id}`, navigate);
                }}
              >
                <div className="column f a-c gap-1">
                  <div
                    className="action_btn edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (item?.chassis) {
                        navigator.clipboard.writeText(item.chassis).then(() => {
                          setCopied({ ...copied, [item.id]: true });
                          setTimeout(() => {
                            setCopied({ ...copied, [item.id]: false });
                          }, 2000);
                        }).catch((err) => {
                          console.error('Failed to copy chassis: ', err);
                        });
                      }
                    }}
                  >
                    {isCopied ? checkIcon : copyIcon}
                  </div>
                  <div style={{ flexGrow: 1, textAlign: 'start' }}>{item?.chassis || '-'}</div>
                </div>
                <div className="column">
                  <div className="capitalize">{item?.variant?.vehicle_model?.name || '-'}</div>
                </div>
                <div className="column">
                  <div className="capitalize">{item?.variant?.display_name_translation?.[i18n?.language] || '-'}</div>
                </div>
                <div className="column">{Number(item?.price)?.toLocaleString('en-US') || '-'}</div>
                {!used && (
                  <Fragment>
                    <div className="column">{item?.reserved ? t('reserved') : t("available")}</div>
                    <div className="column">{moment(item?.last_synced).format('DD/MM/YYYY hh:mm A') || '-'}</div>
                  </Fragment>
                )}
                <div className={`column status_col  `}>
                  <div className="centered-flex min-height-38">
                    <div onClick={(e) => e.stopPropagation()}>
                      <SwitchActivation onChange={() => handleChangeStatus({...item, is_active: statuses?.[item.id]})} active={statuses?.[item.id] == true} loading={loadingItems[item.id] || false} />
                    </div>
                  </div>
                </div>
                {!used && (
                  <div className="column actions actions_col">
                    <div
                      className="action_btn edit"
                      onClick={(e) => {
                        navigateRoute(e, `/vehicles/${item.id}`, navigate);
                      }}
                    >
                      {eyeIcon}
                    </div>
                  </div>
                )}
                {used && (
                  <Fragment>
                    <div className="column actions actions_col">
                      <div
                        className="action_btn edit"
                        onClick={(e) => {
                          navigateRoute(e, `/used-vehicles/vehicle-edit/${item.id}`, navigate);
                        }}
                      >
                        {editIcon}
                      </div>
                    </div>
                    {/* <DeleteButton deleteRoute={`/admin/accessories`} queryKey='accessories' id={item.id}/> */}
                  </Fragment>
                )}
              </div>
              )
            })}
          </>
        ) : (
          <h6>{noDataMessage}</h6>
        )}
      </div>
    </div>
  );
};

export default VehiclesTableContainer;
